import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";

import { onAuthStateChanged } from "firebase/auth";

import { useNavigation } from "@hooks";
import * as analytics from "@utils/analytics";

import { ensureUserDoc } from "../data/userService";
import { auth } from "../firebase";
import OnboardingModal from "../onboarding/OnboardingModal";
import AuthModal from "./AuthModal";
import { signOutToGuest } from "./authService";
import SignOutModal from "./SignOutModal";

const CommunityAuthContext = createContext(null);

// A plain copy of the bits we use. Linking keeps the same Firebase user object,
// so React wouldn't notice the change if we stored the object itself.
const snapshot = (u) =>
  u
    ? {
      uid: u.uid,
      isAnonymous: u.isAnonymous,
      displayName: u.displayName,
      email: u.email,
      photoURL: u.photoURL,
    }
    : null;

/**
 * Keeps every visitor signed in (anonymously at first), and owns three popups:
 *  - AuthModal:       sign in / sign up, opened by requireAuth() or openAuth()
 *  - OnboardingModal: profile (required) then portfolio (skippable), shown after
 *                     sign-in whenever the member's document says setup isn't finished
 *  - SignOutModal:    "Log out?", opened by openSignOut()
 *
 *   const { requireAuth } = useCommunityAuth();
 *   requireAuth(() => toggleFollow(uid), { reason: 'Sign up to follow Emily.' });
 *
 * The action runs once the visitor is signed in AND has finished setup.
 * Must sit inside <Router> (it navigates home after logging out).
 */
const CommunityAuthProvider = ({ children }) => {
  const { navigate } = useNavigation();
  const [user, setUser] = useState(undefined); // undefined = still checking
  const [modal, setModal] = useState(null); // { mode, reason } or null
  const [onboarding, setOnboarding] = useState(null);   // { profile, step } || null
  const [profileVersion, setProfileVersion] = useState(0);
  const [signOutOpen, setSignOutOpen] = useState(false);
  const pendingAction = useRef(null);
  const [pendingTick, setPendingTick] = useState(0);
  const checkedUid = useRef(null);

  /**
   * Make sure the member has a betaCommunityUsers doc, and open setup if it's unfinished.
   * Returns true when the member is ready to use the app.
   */
  // Sign-in fires both AuthModal's callback and onAuthStateChanged; share one check per uid
  // so the document is only created once.
  const inFlight = useRef({});
  const checkMember = useCallback((fbUser) => {
    const uid = fbUser.uid;
    if (!inFlight.current[uid]) {
      inFlight.current[uid] = (async () => {
        const { data } = await ensureUserDoc(fbUser);
        checkedUid.current = uid;

        const step = (() => {
          if (!data) return 'profile';
          if (data?.profileComplete === false) return 'profile';
          if (data?.portfolioSetup === null) return 'portfolio';
          return null;
        })();

        if (step) setOnboarding({ step, profile: data });
        return !step;

      })().finally(() => {
        delete inFlight.current[uid];
      });
    }
    return inFlight.current[uid];
  }, []);

  useEffect(() =>
    onAuthStateChanged(auth, (u) => {
      if (!u) {
        checkedUid.current = null;
        // return;
      };
      setUser(snapshot(u));
      // Members who arrive already signed in (e.g., a returning visit) get checked too
      if (!u?.isAnonymous && checkedUid.current != u?.uid) {
        checkMember(u).catch((err) => (
          console.error("Could not load community profile", err)
        ));
      };
    }),
  [checkMember]);

  // Run the action the visitor originally clicked, after the app has re-rendered
  // with their new account (children render before this effect runs).
  useEffect(() => {
    if (!pendingTick) return;
    const action = pendingAction.current;
    pendingAction.current = null;
    if (action) action();
  }, [pendingTick]);

  const openAuth = useCallback(({ mode = 'signup', reason = '' } = {}) => {
    setModal({ mode, reason });
  }, []);

  const closeAuth = useCallback(() => {
    pendingAction.current = null;
    setModal(null);
  }, []);

  const requireAuth = useCallback(
    (action, { mode = 'signup', reason = '' } = {}) => {
      if (user && !user.isAnonymous) {
        if (action) action();
        return;
      }
      pendingAction.current = action || null;
      setModal({ mode, reason });
    },
    [user]
  );

  // Called by AuthModal after a successful Google or email sign-in/up.
  const handleAuthenticated = useCallback(async ({ user: fbUser, isNewUser }, method) => {
    await ensureUserDoc(fbUser);
    analytics.setUserId(fbUser.uid);

    if (isNewUser) analytics.trackSignedUp(method);
    else analytics.trackSignedIn(method);

    setUser(snapshot(auth.currentUser));
    setModal(null);
    setPendingTick((n) => n + 1);
  }, []);

  // Called by OnboardingModal when the member finishes (or skips) the last step.
  const finishOnboarding = useCallback(() => {
    setOnboarding(null);
    setProfileVersion((v) => v + 1);
    setPendingTick((n) => n + 1);
  }, []);

  const openSignOut = useCallback(() => setSignOutOpen(true), []);
  const closeSignOut = useCallback(() => setSignOutOpen(false), []);

  const leaveToGuest = useCallback(async () => {
    pendingAction.current = null;
    setOnboarding(null);
    await signOutToGuest(); // back to an anonymous browsing session
    analytics.reset();
    navigate('/'); // leave pages like /me/edit that need an account
  }, [navigate]);

  // Called by SignOutModal's "Log out" button. Errors bubble up so the modal can show them.
  const confirmSignOut = useCallback(async () => {
    await signOutToGuest(); // back to an anonymous browsing session
    analytics.reset();
    setSignOutOpen(false);
    navigate('/'); // leave pages like /me/edit that need an account
  }, [navigate]);

  const value = useMemo(
    () => ({
      user, // { uid, isAnonymous, displayName, email, photoURL } | null | undefined
      loading: user === undefined,
      isSignedIn: Boolean(user && !user.isAnonymous),
      isOnboarding: Boolean(onboarding),
      profileVersion,
      requireAuth,
      openAuth,
      closeAuth,
      openSignOut,
      signOut: signOutToGuest, // logs out straight away, without the confirmation
    }),
    [
      closeAuth,
      onboarding,
      openAuth,
      openSignOut,
      profileVersion,
      requireAuth,
      user,
    ],
  );

  return (
    <CommunityAuthContext.Provider value={value}>
      {children}
      {modal && (
        <AuthModal
          initialMode={modal.mode}
          reason={modal.reason}
          onClose={closeAuth}
          onAuthenticated={handleAuthenticated}
        />
      )}
      {onboarding && user && (
        <OnboardingModal
          uid={user.uid}
          initialStep={onboarding.step}
          profile={onboarding.profile}
          email={user.email}
          onFinish={finishOnboarding}
          onLogOut={leaveToGuest}
        />
      )}
      {signOutOpen && (
        <SignOutModal
          name={user?.displayName ? user.displayName.split(' ')[0] : ''}
          onCancel={closeSignOut}
          onConfirm={confirmSignOut}
        />
      )}
    </CommunityAuthContext.Provider>
  );
}

const useCommunityAuth = () => {
  const ctx = useContext(CommunityAuthContext);
  if (!ctx) throw new Error("useCommunityAuth must be used inside <CommunityAuthProvider>");
  return ctx;
};

export { CommunityAuthProvider, useCommunityAuth };
